import { Component, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { ISearchResult } from '../../../models/well/search/search.result';
import { OregonWellDataService } from '../../../services/oregon-well-data.service';
import { AngularCsv } from 'angular-csv-ext/dist/Angular-csv';
import { ToastrService } from 'ngx-toastr';
import { MatDialog } from '@angular/material/dialog';
import { DialogMergeWellsComponent } from './merge-wells-dialog-component';
import { EditSampleComponent } from '../samples/edit/edit-samples.component';
import { VoucherService } from 'app/services/voucher-service';
import { DialogEditVoucherComponent } from 'app/components/vouchers/edit/voucher-edit-dialog';
import { AuthService } from '@shared/auth/auth.service';
import { IFuzzySearchResult } from 'app/models/common/fuzzySearchResult';
import { get } from 'http';

@Component({
  selector: 'app-search',
  templateUrl: './search.component.html',
  styleUrls: ['./search.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class SearchComponent implements OnInit {
  pageSize = 20;
  processing = false;
  postErrorMessages = [];
  showResults = false;
  searchResultsMaxRows = "5000";
  checkboxActionList: string[] = [];
  showVoucherType: boolean = false;

  // #region form

  requestForm = new FormGroup({
    wellId: new FormControl(this.oregonWellDataService.search.wellId),
    sampleId: new FormControl(this.oregonWellDataService.search.sampleId),
    contaminant: new FormControl(this.oregonWellDataService.search.contaminant),
    county: new FormControl(this.oregonWellDataService.search.county),
    address: new FormControl(this.oregonWellDataService.search.address),
    city: new FormControl(this.oregonWellDataService.search.city),
    zip: new FormControl(this.oregonWellDataService.search.zip),
    wellTag: new FormControl(this.oregonWellDataService.search.wellTag),
    wellLog: new FormControl(this.oregonWellDataService.search.wellLog),
    startDate: new FormControl(this.oregonWellDataService.search.startDate),
    endDate: new FormControl(this.oregonWellDataService.search.endDate),
    createStartDate: new FormControl(this.oregonWellDataService.search.createStartDate),
    createEndDate: new FormControl(this.oregonWellDataService.search.createEndDate),
    receivedStartDate: new FormControl(this.oregonWellDataService.search.receivedStartDate),
    receivedEndDate: new FormControl(this.oregonWellDataService.search.receivedEndDate),
    searchBy: new FormControl(this.oregonWellDataService.search.searchBy),
    voucherType: new FormControl(this.oregonWellDataService.search.voucherType),
    voucherId: new FormControl(this.oregonWellDataService.search.voucherOhaId),
    voucherIdentifier: new FormControl(this.oregonWellDataService.search.voucherIdentifier),
  });

  displayedColumns: string[] = ['selectRow', 'wellId', 'sampleId', 'voucherId', 'voucherType', 'wellAddress1', 'wellCity', 'wellZip', 'wellCounty', 'wellLog', 'wellTag', 'score'];

  flatResults: any[] = [];
  dataSource = new MatTableDataSource<ISearchResult>(this.flatResults);
  @ViewChild('TablePaginator') paginator: MatPaginator;
  @ViewChild('TableSort') sort: MatSort;

  filterTable(event: Event) {
    let value = (event.target as HTMLInputElement).value;
    this.dataSource.filter = value.trim().toLowerCase();
  }

  // #endregion

  constructor(public dialog: MatDialog, public oregonWellDataService: OregonWellDataService,
    public voucherService: VoucherService, private toastr: ToastrService, public authService: AuthService) { }

  ngOnInit(): void {
    if (this.searchResultsCount > 0) {
      this.showResults = true;
    }

    this.searchByChange(this.requestForm.get('searchBy').value);
  }

  clickCheckboxAction(wellId: string) {
    let idx = this.checkboxActionList.indexOf(wellId);

    if (idx >= 0) {
      this.checkboxActionList.splice(idx, 1);
    }
    else {
      this.checkboxActionList.push(wellId);
    }
  }

  download() {
    let csvOptions = {
      fieldSeparator: ',',
      quoteStrings: '"',
      decimalseparator: '.',
      showLabels: false,
      showTitle: false,
      title: '',
      useBom: false,
      noDownload: false,

      // this order is the CSV layout
      // the headers match & are defined in: SampleParameterModelNames object
      headers: ["WellId", "SampleId", "SampleDate", "PostTreatmentY/N", "LinkedSampleY/N", "LinkedSampleComments",
        "SampleComments", "WellAddress1", "WellCity", "WellState", "WellZip",
        "WellTaxLot", "WellCounty", "SamplePoint", "WellTag", "WellLog",
        "WellDepth", "Well/PropertyComment", "AnalysisDate", "AnalyteName", "AnalyteResult", "AnalyteUnit",
        "AnalyteMethodName", "AnalyteReTestY/N", "LabName", "ORELAP", "Lab/TestResultComments",
        "CreateDate", "RetOrNot", "QA", "WellAddress2",
        "WellLatitude", "WellLongitude", "WellTownship", "WellRange", "WellSection",
        "LabAddress1", "LabAddress2", "LabCity", "LabState", "LabZip",
        "LabCounty", "LabEmail", "LabPhone", "LabContact", "AnalyteMethodContext",
        "AnalyteMethodID", "AnalyteResultID", "PNWWQXSampleId", "VoucherId", "VoucherType"]
    };

    this.setFormFields();
    this.oregonWellDataService.DownloadCsv(this.oregonWellDataService.search).subscribe(
      result => {
        this.processing = false;
        return new AngularCsv(result, this.oregonWellDataService.getTodaysDate(0) + "_Export", csvOptions);
      },
      error => {
        this.processing = false;
      }
    );
  }

  editSample(sampleId: number) {
    const dialogRef = this.dialog.open(EditSampleComponent, {
      width: '90%',
      data: sampleId
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result != "Cancel") {
        this.onSubmit();
      }
    });
  }

  editVoucher(voucherId: number) {
    const dialogRef = this.dialog.open(DialogEditVoucherComponent, {
      width: '90%',
      data: voucherId,
    });
    dialogRef.afterClosed().subscribe(result => {
      this.onSubmit();
    });
  }

  openMergeWellDialog() {
    let wells = this.oregonWellDataService.searchResults.filter(w => this.checkboxActionList.indexOf(w.data.wellId) >= 0).map((result: IFuzzySearchResult) => {
            return {
              wellId: result.data?.wellId || null,
              voucherType: result.data?.voucherType || null,
              voucherId: result.data?.voucherId || null,
              voucherOhaId: result.data?.voucherOhaId || null,
              sampleId: result.data?.sampleId || null,
              wellAddress1: result.data?.wellAddress1 || '',
              wellCity: result.data?.wellCity || '',
              wellZip: result.data?.wellZip || '',
              wellCounty: result.data?.wellCounty || '',
              wellLog: result.data?.wellLog || '',
              wellTag: result.data?.wellTag || '',
              score: result.score || 0
            } as any;
          });
    if (wells != undefined && wells.length > 0) {
      const dialogRef = this.dialog.open(DialogMergeWellsComponent, {
        width: '1080px',
        data: wells,
      });
      dialogRef.afterClosed().subscribe(result => {
        if (result != "Cancel") {
          this.onSubmit();
        }
      });
    }
  }

  reset() {
    let previousSearchBy = this.requestForm.get('searchBy').value;
    this.requestForm.reset();
    this.requestForm.get('searchBy').setValue(previousSearchBy);
  }

  onSubmit() {
    this.setFormFields();
    this.processing = true;
    this.showResults = false;
    this.checkboxActionList = [];
    this.oregonWellDataService.search.searchResultsMaxRows = this.searchResultsMaxRows;

    this.oregonWellDataService.Search(this.oregonWellDataService.search).subscribe(
      result => {
        this.oregonWellDataService.searchResults = result;
        this.oregonWellDataService.haveSearchResults = true;
        this.oregonWellDataService.searchResultsCount = this.oregonWellDataService.searchResults.length;
        this.searchResultsCount = this.oregonWellDataService.searchResults.length;
        this.processing = false;
        this.showResults = true;
      },
      error => {
        this.processing = false;
        this.showResults = false;
        this.postErrorMessages = this.oregonWellDataService.onHttpError(error);
      },
      () => {
        this.flatResults = this.getFlatSearchResults();
        this.dataSource = new MatTableDataSource<ISearchResult>(this.flatResults);
        this.dataSource.paginator = this.paginator;
        this.dataSource.sort = this.sort;
      }
    );
  }

  getFlatSearchResults(): any[] {
    return this.oregonWellDataService.searchResults.map((result: IFuzzySearchResult) => {
      return {
        wellId: result.data?.wellId || null,
        voucherType: result.data?.voucherType || null,
        voucherId: result.data?.voucherId || null,
        voucherOhaId: result.data?.voucherOhaId || null,
        sampleId: result.data?.sampleId || null,
        wellAddress1: result.data?.wellAddress1 || '',
        wellCity: result.data?.wellCity || '',
        wellZip: result.data?.wellZip || '',
        wellCounty: result.data?.wellCounty || '',
        wellLog: result.data?.wellLog || '',
        wellTag: result.data?.wellTag || '',
        score: result.score || 0
      } as any;
    });
  }

  setFormFields() {
    this.oregonWellDataService.search.wellId = this.requestForm.value.wellId;
    this.oregonWellDataService.search.sampleId = this.requestForm.value.sampleId;
    this.oregonWellDataService.search.wellTag = this.requestForm.value.wellTag;
    this.oregonWellDataService.search.wellLog = this.requestForm.value.wellLog;
    this.oregonWellDataService.search.contaminant = this.requestForm.value.contaminant;
    this.oregonWellDataService.search.county = this.requestForm.value.county;
    this.oregonWellDataService.search.address = this.requestForm.value.address;
    this.oregonWellDataService.search.city = this.requestForm.value.city;
    this.oregonWellDataService.search.zip = this.requestForm.value.zip;
    this.oregonWellDataService.search.wellLog = this.requestForm.value.wellLog;
    this.oregonWellDataService.search.startDate = this.requestForm.value.startDate;
    this.oregonWellDataService.search.endDate = this.requestForm.value.endDate;
    this.oregonWellDataService.search.createStartDate = this.requestForm.value.createStartDate;
    this.oregonWellDataService.search.createEndDate = this.requestForm.value.createEndDate;
    this.oregonWellDataService.search.receivedStartDate = this.requestForm.value.receivedStartDate;
    this.oregonWellDataService.search.receivedEndDate = this.requestForm.value.receivedEndDate;
    this.oregonWellDataService.search.searchBy = this.requestForm.value.searchBy;
    this.oregonWellDataService.search.voucherType = this.requestForm.value.voucherType;
    this.oregonWellDataService.search.voucherIdentifier = this.requestForm.value.voucherIdentifier;
  }

  searchByChange(formValue: string) {
    if (formValue == 'Sample') {
      this.showVoucherType = true;
    }
    else {
      this.showVoucherType = false;
    }
  }

  searchResults(): IFuzzySearchResult[] {
    return this.oregonWellDataService.searchResults;
  }

  searchResultsCount = this.oregonWellDataService.searchResultsCount;
}